// Faixa de trilha concluída, com o certificado em PDF.
//
// ONDE: logo abaixo das abas, no cabeçalho da trilha aberta — e não como card
// fixo no topo do mapa. Três razões: (1) o certificado é POR TRILHA, então ele
// precisa estar dentro do escopo visual da aba, senão vira ambiguidade quando
// uma trilha está completa e outra não; (2) o topo já tem quatro blocos
// disputando atenção (stats, barra de título, retomada, revisão) e nenhum
// deles some quando esta trilha acaba; (3) só existe no estado 100%, então não
// ocupa espaço permanente nenhum.
import {useContext, useState} from 'react'
import {Alert, Box, Button, CircularProgress, Typography} from '@mui/material'
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined'
import {AuthContext} from '../../../contexts/auth'
import {getCurrentColors} from '../../../utils/colors'
import {baixarCertificado} from '../certificado'

const COLORS = getCurrentColors()

interface CardCertificadoProps {
    trilha: string
    dominadas: number
}

export default function CardCertificado({
    trilha,
    dominadas,
}: CardCertificadoProps) {
    const {userInfo} = useContext(AuthContext)
    const [ocupado, setOcupado] = useState(false)
    const [erro, setErro] = useState(false)

    // Só o que o contexto de auth já tem. Usuário ainda não carregado (ou
    // sessão sem perfil) devolve string vazia — e aí o PDF sai com uma linha
    // para preencher à mão, em vez de qualquer nome inventado.
    const nome = [userInfo?.first_name, userInfo?.last_name]
        .map((parte) => (parte ?? '').trim())
        .filter(Boolean)
        .join(' ')

    const baixar = async () => {
        if (ocupado) return
        setOcupado(true)
        setErro(false)
        try {
            await baixarCertificado({trilha, dominadas, nome})
        } catch {
            // chunk do jspdf que não baixou, storage bloqueado, save negado:
            // vira aviso na tela, nunca exceção solta
            setErro(true)
        } finally {
            setOcupado(false)
        }
    }

    return (
        <Box
            component="section"
            aria-label="Certificado da trilha"
            sx={{
                mt: 3,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                backgroundColor: COLORS.branco,
                border: `1px solid ${COLORS.cinzaDesbotado}`,
                borderLeft: `4px solid ${COLORS.primariaEscura}`,
                borderRadius: '12px',
            }}
        >
            <Box sx={{flex: 1, minWidth: '180px'}}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{letterSpacing: 1}}
                >
                    TRILHA CONCLUÍDA
                </Typography>
                <Typography fontWeight="bold" sx={{fontSize: '17px'}}>
                    {`Você concluiu ${trilha}`}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{mt: 0.25}}
                >
                    {`${dominadas} ${
                        dominadas === 1
                            ? 'pílula dominada'
                            : 'pílulas dominadas'
                    } nesta trilha. ${
                        nome
                            ? 'Baixe seu certificado em PDF.'
                            : 'O certificado sai com uma linha em branco para você preencher o nome.'
                    }`}
                </Typography>
                {erro && (
                    <Alert
                        severity="error"
                        sx={{mt: 1}}
                        onClose={() => setErro(false)}
                        closeText="Fechar aviso"
                    >
                        Não foi possível gerar o certificado. Tente novamente.
                    </Alert>
                )}
            </Box>
            <Button
                variant="contained"
                disabled={ocupado}
                onClick={baixar}
                startIcon={
                    ocupado ? (
                        <CircularProgress size={16} color="inherit" />
                    ) : (
                        <WorkspacePremiumOutlinedIcon />
                    )
                }
                aria-label={`Baixar certificado de conclusão de ${trilha} em PDF`}
                sx={{
                    backgroundColor: COLORS.botaoFormulario,
                    textTransform: 'none',
                    width: {xs: '100%', sm: 'auto'},
                    minHeight: {xs: '44px', sm: 'auto'},
                    '&:focus-visible': {
                        outline: `2px solid ${COLORS.primariaEscura}`,
                        outlineOffset: '2px',
                    },
                }}
            >
                Baixar certificado
            </Button>
        </Box>
    )
}
